import { useMemo, useState, useEffect, useCallback } from "react";
import ReactECharts from "echarts-for-react";
import dayjs from "dayjs";
import { Drawer, Box, Stack, Typography, IconButton, LinearProgress, Divider, Button, Accordion, AccordionSummary, AccordionDetails, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CodeIcon from "@mui/icons-material/Code";
import BlockIcon from "@mui/icons-material/Block";
import API from "../../../../../api";
import BlacklistRuleDialog from "../../../../../components/BlacklistRuleDialog";
import { SEV_COLOR, TYPE_COLOR, TYPE_DESCRIPTION, scoreColor } from "../constants";

const ts = (v) => {
  const raw = v?.$date ?? v;
  return raw ? dayjs(raw).format("YYYY-MM-DD HH:mm") : "-";
};

const ScoreBadge = ({ label, value, color }) => (
  <Box sx={{ mb: 0.8 }}>
    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.3 }}>
      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{label}</Typography>
      <Typography sx={{ fontSize: 12, fontFamily: "monospace", color }}>
        {typeof value === "number" ? value.toFixed(4) : "-"}
      </Typography>
    </Stack>
    <LinearProgress
      variant="determinate"
      value={Math.min(100, Math.round((value ?? 0) * 100))}
      sx={{
        height: 4, borderRadius: 2,
        bgcolor: "#333",
        "& .MuiLinearProgress-bar": { bgcolor: color },
      }}
    />
  </Box>
);

const KV = ({ label, children }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.4 }}>
    <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{label}</Typography>
    <Box>{children}</Box>
  </Stack>
);

// Sintassi delle reason (testo libero dal motore):
//   Key (a,b=v1:v2) ...   prefisso opzionale con la chiave dell'edge
//   [tag]                 tag
//   ( ... )               elenco di valori "chiave=valore" o "chiave: valore" separati da ","
//   |                     separa più motivazioni (anche fuori dalle parentesi: ogni segmento ha i suoi tag/titolo/valori)
// Esempi:
//   Key (src_ip,dst_ip=1.2.3.4:5.6.7.8) EWMA frequency=0.0 (gap=11455, score=1.0000)
//   ALERT [mac_change_for_ip]: first time (src_mac=aa:bb, src_ip=1.2.3.4 | src_ip=1.2.3.4)
//   1.2.3.4: silent for 13.9d (history: 28.3d, threshold: 7.0d)
// Se non si riconosce nulla si mostra il testo originale.
const KEY_LABEL = { src_ip: "Source", dst_ip: "Destination", src_port: "Src port", dst_port: "Dst port" };
const METRIC_HELP = {
  score: "Anomaly score of this reason (0 to 1).",
  frequency: "Smoothed (EWMA) event frequency for this key.",
  gap: "Gap since this key was last seen.",
  key_obs: "Observations of this key (seen / minimum required).",
  S_high: "CUSUM upper sum: grows when values drift above the baseline.",
  S_low: "CUSUM lower sum: grows when values drift below the baseline.",
  h: "CUSUM decision threshold: a shift is flagged when the sum exceeds it.",
  ref_mean: "Reference (baseline) mean the shift is measured against.",
};

// "chiave=valore" o "chiave: valore" (si spezza al primo separatore: i valori possono contenere ":", es. MAC)
const parseItem = (raw) => {
  const s = raw.trim();
  if (!s) return null;
  const m = s.match(/^([A-Za-z_][\w.-]*)\s*[=:]\s*(.+)$/s);
  return m ? { name: m[1], value: m[2].trim() } : { text: s };
};

const splitTopLevel = (s, sep) => {
  const out = [];
  let depth = 0, cur = "";
  for (const ch of s) {
    if ("([".includes(ch)) depth++;
    else if (")]".includes(ch)) depth = Math.max(0, depth - 1);
    if (ch === sep && depth === 0) { out.push(cur); cur = ""; } else cur += ch;
  }
  out.push(cur);
  return out;
};

const parseSegment = (text) => {
  // scorre il testo separando le parti fuori dalle parentesi dai gruppi "( ... )" di primo livello
  let outside = "";
  const groupsRaw = [];
  const rawParts = [];
  let depth = 0, cur = "", buf = "";
  for (const ch of text) {
    if (ch === "(") {
      if (depth === 0) { rawParts.push({ text: buf }); buf = ""; cur = ""; } else cur += ch;
      depth++;
      continue;
    }
    if (ch === ")" && depth > 0) {
      depth--;
      if (depth === 0) { groupsRaw.push(cur); rawParts.push({ group: cur }); } else cur += ch;
      continue;
    }
    if (depth > 0) cur += ch; else { outside += ch; buf += ch; }
  }
  if (depth > 0) { groupsRaw.push(cur); rawParts.push({ group: cur }); } // parentesi non chiusa: si tiene quel che c'è
  else rawParts.push({ text: buf });

  const tags = [];
  outside = outside.replace(/\[([^\]]+)\]:?/g, (_, t) => { tags.push(t.trim()); return ""; });

  const outerItems = [];
  outside = outside.replace(/([A-Za-z_]\w*)=([^\s,()]+),?/g, (_, n, v) => { outerItems.push({ name: n, value: v }); return ""; });
  const title = outside.replace(/\s+/g, " ").replace(/[\s,:]+$/, "").trim();

  // più gruppi senza "|" descrivono la stessa motivazione (si fondono in una riga);
  // un "|" dentro un gruppo separa motivazioni diverse (una riga ciascuna)
  const toItems = (r) => splitTopLevel(r, ",").map(parseItem).filter(Boolean);
  const merged = [];
  const alternatives = [];
  for (const g of groupsRaw) {
    const parts = splitTopLevel(g, "|");
    if (parts.length === 1) merged.push(...toItems(parts[0]));
    else parts.forEach(p => alternatives.push(toItems(p)));
  }
  const reasons = [merged, ...alternatives].filter(r => r.length > 0);

  // frase con i valori inseriti al loro posto: "first time (dst_ip=..) for (src_ip=..) - 0 known combinations"
  const cleanText = (s) => s.replace(/\[([^\]]+)\]:?/g, "").replace(/\s+/g, " ").replace(/^[\s:]+|[\s:]+$/g, "");
  const parts = rawParts
    .map(p => (p.group !== undefined
      ? { group: splitTopLevel(p.group, "|").map(toItems).filter(a => a.length > 0) }
      : { text: cleanText(p.text) }))
    .filter(p => (p.group ? p.group.length > 0 : p.text));
  const inline = outerItems.length === 0 && groupsRaw.length > 0 && parts.filter(p => p.text !== undefined).length >= 2;

  return { tags, title, outerItems, reasons, parts, inline };
};

const parseReasonDetail = (detail) => {
  if (typeof detail !== "string") return null;
  let rest = detail.trim();

  let key = null;
  const km = rest.match(/^Key \(([^)=]+)=([^)]*)\)\s*(.*)$/s);
  if (km) {
    const names = km[1].split(",").map(s => s.trim());
    const values = km[2].split(":").map(s => s.trim());
    if (names.length === values.length) key = names.map((n, i) => ({ name: n, value: values[i] }));
    rest = km[3];
  }

  const segments = splitTopLevel(rest, "|")
    .map(parseSegment)
    .filter(sg => sg.tags.length > 0 || sg.title || sg.outerItems.length > 0 || sg.reasons.length > 0);

  if (!key && segments.length === 0) return null;
  return { key, segments };
};

const MetricTile = ({ item }) => {
  if (item.text) {
    return (
      <Box sx={{ px: 0.8, py: 0.3, bgcolor: "#141414", border: "1px solid #2f2f2f", borderRadius: 0.5 }}>
        <Typography sx={{ fontSize: 12, fontFamily: "monospace", color: "#ddd" }}>{item.text}</Typography>
      </Box>
    );
  }
  const help = METRIC_HELP[item.name];
  return (
    <Tooltip title={help ?? ""} placement="top" disableHoverListener={!help}>
      <Box sx={{ px: 0.8, py: 0.3, bgcolor: "#141414", border: "1px solid #2f2f2f", borderRadius: 0.5, cursor: help ? "help" : "default" }}>
        <Typography sx={{ fontSize: 10, color: "text.secondary", lineHeight: 1.2 }}>{item.name}</Typography>
        <Typography sx={{ fontSize: 12, fontFamily: "monospace", color: "#ddd", lineHeight: 1.4, wordBreak: "break-all" }}>{item.value}</Typography>
      </Box>
    </Tooltip>
  );
};

// chip orizzontale compatta, usato quando i valori stanno dentro la frase
const InlineItem = ({ item }) => {
  if (item.text) {
    return <Typography sx={{ fontSize: 12, fontFamily: "monospace", color: "#ddd" }}>{item.text}</Typography>;
  }
  const help = METRIC_HELP[item.name];
  return (
    <Tooltip title={help ?? ""} placement="top" disableHoverListener={!help}>
      <Box sx={{ display: "inline-flex", alignItems: "baseline", gap: 0.6, px: 0.7, py: 0.15, bgcolor: "#141414", border: "1px solid #2f2f2f", borderRadius: 0.5, cursor: help ? "help" : "default" }}>
        <Typography sx={{ fontSize: 10, color: "text.secondary" }}>{item.name}</Typography>
        <Typography sx={{ fontSize: 12, fontFamily: "monospace", color: "#ddd", wordBreak: "break-all" }}>{item.value}</Typography>
      </Box>
    </Tooltip>
  );
};

const ReasonSegment = ({ segment, skipTags }) => {
  const { title, outerItems, reasons } = segment;
  const tags = segment.tags.filter(t => !skipTags.includes(t));
  const tagChips = tags.map(t => (
    <Box key={t} sx={{ px: 0.7, py: 0.1, bgcolor: "#333", borderRadius: 0.5 }}>
      <Typography sx={{ fontSize: 11, color: "#bbb", fontFamily: "monospace" }}>{t}</Typography>
    </Box>
  ));

  if (segment.inline) {
    let seenGroup = false;
    return (
      <Stack direction="row" flexWrap="wrap" alignItems="center" columnGap={0.7} rowGap={0.6}>
        {tagChips}
        {segment.parts.map((p, i) => {
          if (p.group) {
            seenGroup = true;
            return (
              <Stack key={i} direction="row" flexWrap="wrap" alignItems="center" columnGap={0.5} rowGap={0.4}>
                {p.group.map((alt, ai) => (
                  <Stack key={ai} direction="row" alignItems="center" columnGap={0.5}>
                    {ai > 0 && <Typography sx={{ fontSize: 12, color: "#555" }}>|</Typography>}
                    {alt.map((it, ii) => <InlineItem key={ii} item={it} />)}
                  </Stack>
                ))}
              </Stack>
            );
          }
          return (
            <Typography key={i} sx={seenGroup
              ? { fontSize: 12.5, color: "#999" }
              : { fontSize: 12.5, fontWeight: 600, color: "#ccc" }}>
              {p.text}
            </Typography>
          );
        })}
      </Stack>
    );
  }

  // un solo gruppo: le coppie fuori dalle parentesi si fondono con quelle dentro
  const rows = reasons.length <= 1
    ? [{ items: [...outerItems, ...(reasons[0] ?? [])] }].filter(r => r.items.length > 0)
    : [...(outerItems.length ? [{ items: outerItems }] : []), ...reasons.map((items, i) => ({ items, n: i + 1 }))];
  return (
    <Box>
      {(title || tags.length > 0) && (
        <Stack direction="row" flexWrap="wrap" alignItems="center" gap={0.6} sx={{ mb: rows.length ? 0.6 : 0 }}>
          {tagChips}
          {title && <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "#ccc" }}>{title}</Typography>}
        </Stack>
      )}
      {rows.map(({ items, n }, ri) => (
        <Stack key={ri} direction="row" flexWrap="wrap" alignItems="center" gap={0.6} sx={{ mt: ri > 0 ? 0.6 : 0 }}>
          {n && <Typography sx={{ fontSize: 10, color: "text.secondary", minWidth: 16 }}>#{n}</Typography>}
          {items.map((it, i) => <MetricTile key={i} item={it} />)}
        </Stack>
      ))}
    </Box>
  );
};

const ReasonDetail = ({ detail, PORT_TO_PROTOCOL, skipTags = [] }) => {
  const parsed = useMemo(() => parseReasonDetail(detail), [detail]);
  if (!parsed) {
    return (
      <Typography sx={{ fontSize: 12, fontFamily: "monospace", color: "#aaa", wordBreak: "break-all", lineHeight: 1.5 }}>
        {detail}
      </Typography>
    );
  }
  const { key, segments } = parsed;
  return (
    <Box>
      {key && (
        <Stack direction="row" flexWrap="wrap" alignItems="center" gap={0.5} sx={{ mb: segments.length ? 0.6 : 0 }}>
          {key.map((k, i) => {
            const proto = k.name.endsWith("port") ? PORT_TO_PROTOCOL?.[k.value] : null;
            return (
              <Box key={k.name} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {i > 0 && <Box component="span" sx={{ color: "#555", fontSize: 12 }}>{"→"}</Box>}
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.6, px: 0.7, py: 0.15, bgcolor: "#2a2a2a", border: "1px solid #3a3a3a", borderRadius: 0.5 }}>
                  <Typography sx={{ fontSize: 10, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4 }}>
                    {KEY_LABEL[k.name] ?? k.name}
                  </Typography>
                  <Typography sx={{ fontSize: 12, fontFamily: "monospace", color: "#ddd" }}>
                    {k.value}{proto ? ` (${proto})` : ""}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Stack>
      )}
      {segments.map((sg, i) => (
        <Box key={i} sx={i > 0 ? { mt: 1, pt: 1, borderTop: "1px dashed #333" } : undefined}>
          <ReasonSegment segment={sg} skipTags={skipTags} />
        </Box>
      ))}
    </Box>
  );
};

const ReasonCard = ({ reason, PORT_TO_PROTOCOL }) => {
  const [showRaw, setShowRaw] = useState(false);
  const labelColor = TYPE_COLOR[reason.label] ?? TYPE_COLOR[reason.model_type] ?? "#888888";
  return (
    <Box sx={{
      pl: 1.5, pr: 1, py: 0.8, mb: 0.8,
      borderLeft: `3px solid ${labelColor}`,
      bgcolor: "#1a1a1a",
      borderRadius: "0 4px 4px 0",
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.4 }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{
            px: 0.7, py: 0.1,
            bgcolor: `${labelColor}22`,
            border: `1px solid ${labelColor}55`,
            borderRadius: 0.5,
          }}>
            <Typography sx={{ color: labelColor, fontSize: 11, fontWeight: 700 }}>
              {reason.label}
            </Typography>
          </Box>
          {reason.scope && (
            <Box sx={{ px: 0.7, py: 0.1, bgcolor: "#333", borderRadius: 0.5 }}>
              <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{reason.scope}</Typography>
            </Box>
          )}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {reason.detail && (
            <Tooltip title={showRaw ? "Show formatted reason" : "Show raw reason"} placement="top">
              <IconButton
                size="small"
                onClick={() => setShowRaw(v => !v)}
                sx={{ p: 0.3, color: showRaw ? "#459095" : "#666", "&:hover": { color: "#459095" } }}
              >
                <CodeIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
          <Typography sx={{ fontSize: 12, fontFamily: "monospace", color: "#459095", fontWeight: 700 }}>
            {typeof reason.score === "number" ? reason.score.toFixed(4) : "-"}
          </Typography>
        </Stack>
      </Stack>

      {reason.model && (
        <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.3 }}>
          {reason.model}{reason.model_type ? ` · ${reason.model_type}` : ""}
        </Typography>
      )}

            {reason.detail && (showRaw ? (
        <Typography sx={{
          fontSize: 12, fontFamily: "monospace", color: "#aaa", whiteSpace: "pre-wrap",
          wordBreak: "break-all", lineHeight: 1.5, p: 0.8, bgcolor: "#111", border: "1px solid #2a2a2a", borderRadius: 0.5,
        }}>
          {reason.detail}
        </Typography>
      ) : (
        <ReasonDetail detail={reason.detail} PORT_TO_PROTOCOL={PORT_TO_PROTOCOL} skipTags={[reason.label]} />
      ))}

      {reason.ports?.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={0.4} sx={{ mt: 0.5 }}>
          {reason.ports.map(p => {
            const proto = PORT_TO_PROTOCOL?.[p];
            return (
              <Tooltip key={p} title={proto ?? ""} placement="top">
                <Box sx={{
                  px: 0.6, py: 0.1, bgcolor: "#2a2a2a",
                  border: "1px solid #444", borderRadius: 0.5, cursor: "default",
                }}>
                  <Typography sx={{ fontSize: 11, fontFamily: "monospace" }}>
                    {p}{proto ? ` (${proto})` : ""}
                  </Typography>
                </Box>
              </Tooltip>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

const TruncatedWarning = ({ message }) => (
  <Box sx={{
    display: "flex", alignItems: "center", gap: 0.8, p: 1,
    bgcolor: "#EF9F2715", border: "1px solid #EF9F2740",
    borderRadius: 1, mt: 0.5,
  }}>
    <InfoOutlinedIcon sx={{ fontSize: 15, color: "#EF9F27", flexShrink: 0 }} />
    <Typography sx={{ fontSize: 12, color: "#EF9F27" }}>{message}</Typography>
  </Box>
);

export default function AnomalyDetailDrawer({
  anomaly, onClose, PORT_TO_PROTOCOL, filterTypes,
  siblings = [], activeIndex = 0, onNavigate,
}) {
  const sevColor = SEV_COLOR[anomaly?.severity_label] ?? "#888888";

  // Regola blocklist dall'edge: serve il modulo "blacklist" in licenza.
  const [blocklistEdge, setBlocklistEdge] = useState(null);
  const [blocklistEnabled, setBlocklistEnabled] = useState(false);
  useEffect(() => {
    let cancelled = false;
    API.getLicense()
      .then((res) => {
        const mod = (res.data?.modules ?? []).find((m) => m.name === "blacklist");
        if (!cancelled) setBlocklistEnabled(!!mod?.enabled);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Evidence tags and Node/Edge Reasons are driven by the same selection:
  // clicking an Evidence tag toggles it, and it seeds from the outer filter
  // on open/navigate.
  const [selectedEvidenceTags, setSelectedEvidenceTags] = useState([]);
  useEffect(() => {
    setSelectedEvidenceTags(filterTypes?.length > 0 ? [...filterTypes] : []);
  }, [anomaly?.anomaly_id, filterTypes]);

  const toggleEvidenceTag = (k) => {
    setSelectedEvidenceTags(prev => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]);
  };
  const matchesEvidenceSelection = useCallback(
    (label) => selectedEvidenceTags.length === 0 || selectedEvidenceTags.includes(label),
    [selectedEvidenceTags],
  );

  const visibleEvidence = useMemo(() => {
    const entries = Object.entries(anomaly?.evidence ?? {});
    return selectedEvidenceTags.length > 0
      ? entries.filter(([k]) => selectedEvidenceTags.includes(k))
      : entries;
  }, [anomaly, selectedEvidenceTags]);

  // New schema: source-level reasons live in graph.source, not graph.nodes
  const nodesWithReasons = useMemo(() => {
    const src = anomaly?.graph?.source;
    if (!src?.reasons?.length) return [];
    const reasons = src.reasons.filter(r => matchesEvidenceSelection(r.label) || matchesEvidenceSelection(r.model_type));
    if (reasons.length === 0) return [];
    return [{ ...src, role: src.type, reasons }];
  }, [anomaly, matchesEvidenceSelection]);

  const edgesWithReasons = useMemo(
    () => (anomaly?.graph?.edges ?? [])
      .map(e => ({ ...e, reasons: (e.reasons ?? []).filter(r => matchesEvidenceSelection(r.label) || matchesEvidenceSelection(r.model_type)) }))
      .filter(e => e.reasons.length > 0),
    [anomaly, matchesEvidenceSelection],
  );

  const allDstIps = useMemo(
    () => (anomaly?.graph?.edges ?? []).map(e => e.dst).filter(Boolean),
    [anomaly],
  );

  const miniGraphOption = useMemo(() => {
    if (!anomaly?.graph) return null;
    const source = anomaly.graph.source;
    const edges  = anomaly.graph.edges ?? [];

    // Build node list: source + unique destination peers
    const destIds = [...new Set(edges.map(e => e.dst).filter(Boolean))];
    const ecNodes = [
      ...(source ? [{
        id: source.id,
        name: source.id,
        symbolSize: 24,
        itemStyle: { color: "transparent", borderColor: sevColor, borderWidth: 2.5 },
        label: { show: true, fontSize: 9, color: "#ccc", position: "top" },
      }] : []),
      ...destIds.map(id => ({
        id,
        name: id,
        symbolSize: 14,
        itemStyle: { color: SEV_COLOR.destination },
        label: { show: true, fontSize: 9, color: "#ccc", position: "bottom" },
      })),
    ];

    // A graph with fewer than 2 nodes has a degenerate (zero-size) bounding
    // box, which makes ECharts' view coordinate system compute a singular
    // transform matrix and crash. Bail out to the "no graph" placeholder instead.
    if (ecNodes.length < 2) return null;

    const ecLinks = edges.map(e => ({
      source: e.src,
      target: e.dst,
      lineStyle: {
        color: e.peak_score >= 0.8 ? "#E24B4A" : e.peak_score >= 0.5 ? "#eb7434" : "#A7A7A7",
        width: Math.max(1, (e.peak_score ?? 0) * 3),
      },
      label: { show: false },
    }));

    // Build a lookup for tooltip: nodeId → peak_score
    const scoreByNode = new Map(edges.map(e => [e.dst, e.peak_score]));
    if (source) scoreByNode.set(source.id, source.peak_score);

    return {
      backgroundColor: "transparent",
      tooltip: {
        trigger: "item",
        backgroundColor: "#1e1e1e",
        borderColor: "#444",
        textStyle: { color: "#ccc", fontSize: 11 },
        formatter: (p) => p.dataType === "node"
          ? `<b>${p.name}</b><br/>Peak: ${scoreByNode.get(p.name)?.toFixed(3) ?? "-"}`
          : `${p.data.source} → ${p.data.target}`,
      },
      series: [{
        type: "graph",
        layout: "force",
        force: { repulsion: 250, gravity: 0.1, edgeLength: 80 },
        roam: false,
        draggable: false,
        data: ecNodes,
        links: ecLinks,
        edgeSymbol: ["none", "arrow"],
        edgeSymbolSize: 6,
        emphasis: { focus: "adjacency" },
      }],
    };
  }, [anomaly, sevColor]);

  return (
    <Drawer
      anchor="right"
      open={Boolean(anomaly)}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 760 },
          maxWidth: "100vw",
          bgcolor: "#1e1e1e",
          backgroundImage: "none",
          borderLeft: "1px solid",
          borderColor: "divider",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {anomaly && (
        <>
          {/* ── Header fisso ── */}
          <Box sx={{
            px: 2, py: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}>
            {siblings.length > 1 && (
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                  Anomaly {activeIndex + 1} of {siblings.length} for this host
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.3}>
                  <IconButton
                    size="small"
                    disabled={activeIndex <= 0}
                    onClick={() => onNavigate?.(activeIndex - 1)}
                    sx={{ p: 0.4 }}
                  >
                    <ChevronLeftIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={activeIndex >= siblings.length - 1}
                    onClick={() => onNavigate?.(activeIndex + 1)}
                    sx={{ p: 0.4 }}
                  >
                    <ChevronRightIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Stack>
              </Stack>
            )}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={0.8}>
                  <Typography sx={{
                    fontFamily: "monospace", fontWeight: 700, fontSize: 17,
                    color: "#459095", lineHeight: 1.2,
                  }}>
                    {anomaly.entity?.id ?? anomaly.anomaly_id}
                  </Typography>
                  {anomaly.entity?.type && (
                    <Box sx={{ px: 0.6, py: 0.1, bgcolor: "#333", borderRadius: 0.5 }}>
                      <Typography sx={{ fontSize: 11, color: "#888" }}>
                        {anomaly.entity.type}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ ml: 1, flexShrink: 0 }}>
                <Box sx={{
                  px: 0.8, py: 0.2,
                  bgcolor: `${sevColor}22`,
                  border: `1px solid ${sevColor}`,
                  borderRadius: 1,
                }}>
                  <Typography sx={{ color: sevColor, fontSize: 12, fontWeight: 700 }}>
                    {anomaly.severity_label?.toUpperCase() ?? `SEV ${anomaly.severity}`}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={onClose}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>
            </Stack>
          </Box>

          {/* ── Body scrollabile ── */}
          <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 1.5 }}>

            {/* B – Score & metadati */}
            <ScoreBadge label="Current score" value={anomaly.current_score} color={scoreColor(anomaly.current_score)} />
            <ScoreBadge label="Peak score" value={anomaly.peak_score} color={scoreColor(anomaly.peak_score)} />
            <Box sx={{ mt: 1, mb: 0.5 }}>
              <KV label="Engine">
                <Typography sx={{ fontSize: 11 }}>{anomaly.engine ?? "-"}</Typography>
              </KV>
              <KV label="Status">
                <Box sx={{
                  px: 0.7, py: 0.1,
                  bgcolor: anomaly.status === "open" ? "#45909525" : "#55555525",
                  border: `1px solid ${anomaly.status === "open" ? "#459095" : "#555"}`,
                  borderRadius: 0.5,
                }}>
                  <Typography sx={{
                    fontSize: 10,
                    color: anomaly.status === "open" ? "#459095" : "#888",
                  }}>
                    {anomaly.status ?? "-"}
                  </Typography>
                </Box>
              </KV>
              <KV label="First seen">
                <Typography sx={{ fontSize: 11, fontFamily: "monospace" }}>
                  {ts(anomaly.first_seen)}
                </Typography>
              </KV>
              <KV label="Last seen">
                <Typography sx={{ fontSize: 11, fontFamily: "monospace" }}>
                  {ts(anomaly.last_seen)}
                </Typography>
              </KV>
              <KV label="Occurrences">
                <Typography sx={{ fontSize: 11 }}>
                  {anomaly.occurrence_count?.toLocaleString() ?? "-"}
                </Typography>
              </KV>
            </Box>

            <Divider sx={{ my: 1.5 }} />

            {/* C – Evidence summary */}
            {visibleEvidence.length > 0 && (
              <>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.6 }}>
                  <Typography sx={{ fontSize: 12, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Evidence
                  </Typography>
                  {selectedEvidenceTags.length > 0 && (
                    <Typography
                      onClick={() => setSelectedEvidenceTags([])}
                      sx={{ fontSize: 10, color: "#459095", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                    >
                      Clear filter
                    </Typography>
                  )}
                </Stack>
                <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 1 }}>
                  {visibleEvidence.map(([k, v]) => {
                    const c = TYPE_COLOR[k] ?? "#888888";
                    const active = selectedEvidenceTags.includes(k);
                    return (
                      <Tooltip
                        key={k}
                        placement="top"
                        arrow
                        title={TYPE_DESCRIPTION[k] ? `${TYPE_DESCRIPTION[k]} Click to filter the reasons below.` : "Click to filter the reasons below."}
                      >
                      <Box
                        onClick={() => toggleEvidenceTag(k)}
                        sx={{
                          px: 0.8, py: 0.2, cursor: "pointer", userSelect: "none",
                          bgcolor: active ? `${c}40` : `${c}18`,
                          border: `1px solid ${active ? c : `${c}44`}`,
                          borderRadius: 0.5,
                          "&:hover": { bgcolor: `${c}30` },
                        }}
                      >
                        <Typography sx={{ fontSize: 12, color: c, fontWeight: active ? 700 : 400 }}>
                          {k} ×{v}
                        </Typography>
                      </Box>
                      </Tooltip>
                    );
                  })}
                </Stack>
                {selectedEvidenceTags.length > 0 && nodesWithReasons.length === 0 && edgesWithReasons.length === 0 && (
                  <TruncatedWarning message={`No Node/Edge Reason found for: ${selectedEvidenceTags.join(", ")} — this anomaly may only have the aggregate count in Evidence.`} />
                )}
                <Divider sx={{ my: 1.5 }} />
              </>
            )}

            {/* D – Node Reasons */}
            {nodesWithReasons.length > 0 && (
              <>
                <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.8, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Node Reasons
                </Typography>
                {nodesWithReasons.map(node => (
                  <Accordion
                    key={node.id}
                    disableGutters
                    sx={{
                      bgcolor: "#252525",
                      border: "1px solid #333",
                      borderRadius: "4px !important",
                      mb: 0.5,
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ fontSize: 16, color: "#459095" }} />}
                      sx={{ minHeight: 36, "& .MuiAccordionSummary-content": { my: 0.5 } }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontFamily: "monospace", fontSize: 14, color: "#459095", fontWeight: 700 }}>
                          {node.id}
                        </Typography>
                        <Box sx={{ px: 0.6, py: 0.1, bgcolor: "#333", borderRadius: 0.5 }}>
                          <Typography sx={{ fontSize: 11, color: "#888" }}>{node.role}</Typography>
                        </Box>
                        {node.peak_score != null && (
                          <Typography sx={{ fontSize: 12, fontFamily: "monospace", color: sevColor, ml: "auto !important" }}>
                            {node.peak_score.toFixed(3)}
                          </Typography>
                        )}
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 1, pb: 1, pt: 0 }}>
                      {node.reasons.map((r, i) => (
                        <ReasonCard key={i} reason={r} PORT_TO_PROTOCOL={PORT_TO_PROTOCOL} />
                      ))}
                    </AccordionDetails>
                  </Accordion>
                ))}
                <Divider sx={{ my: 1.5 }} />
              </>
            )}

            {/* E – Edge Reasons */}
            {edgesWithReasons.length > 0 && (
              <>
                <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.8, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Edge Reasons
                </Typography>
                {edgesWithReasons.map(edge => (
                  <Accordion
                    key={edge.id}
                    disableGutters
                    sx={{
                      bgcolor: "#252525",
                      border: "1px solid #333",
                      borderRadius: "4px !important",
                      mb: 0.5,
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ fontSize: 16, color: "#459095" }} />}
                      sx={{ minHeight: 36, "& .MuiAccordionSummary-content": { my: 0.5 } }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: "#ccc" }}>
                          {edge.src}
                          <Box component="span" sx={{ color: "#666", mx: 0.5 }}>→</Box>
                          {edge.dst}
                        </Typography>
                        {/* {edge.peak_score != null && (
                          <Typography sx={{ fontSize: 10, fontFamily: "monospace", color: sevColor, ml: "auto !important" }}>
                            {edge.peak_score.toFixed(3)}
                          </Typography>
                        )} */}
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 1, pb: 1, pt: 0 }}>
                      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 0.6 }}>
                        <Tooltip title={blocklistEnabled ? "Create a blocklist rule for this source and destination" : "Blacklist module not licensed"} placement="top">
                          <span>
                            <Button
                              size="small"
                              disabled={!blocklistEnabled}
                              startIcon={<BlockIcon sx={{ fontSize: 14 }} />}
                              onClick={() => setBlocklistEdge(edge)}
                              sx={{ fontSize: 11, textTransform: "none", color: "#459095", "&:hover": { bgcolor: "rgba(69,144,149,0.12)" } }}
                            >
                              Create blocklist rule
                            </Button>
                          </span>
                        </Tooltip>
                      </Stack>
                      {edge.ports?.length > 0 && (
                        <Stack direction="row" flexWrap="wrap" gap={0.4} sx={{ mb: 0.8 }}>
                          <Typography sx={{ fontSize: 12, color: "text.secondary", alignSelf: "center" }}>Ports:</Typography>
                          {edge.ports.map(p => {
                            const proto = PORT_TO_PROTOCOL?.[p];
                            return (
                              <Tooltip key={p} title={proto ?? ""} placement="top">
                                <Box sx={{
                                  px: 0.6, py: 0.1, bgcolor: "#2a2a2a",
                                  border: "1px solid #444", borderRadius: 0.5,
                                }}>
                                  <Typography sx={{ fontSize: 11, fontFamily: "monospace" }}>
                                    {p}{proto ? ` (${proto})` : ""}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            );
                          })}
                        </Stack>
                      )}
                      {edge.reasons.map((r, i) => (
                        <ReasonCard key={i} reason={r} PORT_TO_PROTOCOL={PORT_TO_PROTOCOL} />
                      ))}
                    </AccordionDetails>
                  </Accordion>
                ))}
                {anomaly.graph?.total_edge_count > (anomaly.graph?.edges?.length ?? 0) && (
                  <TruncatedWarning message={`Edge list truncated — ${anomaly.graph.total_edge_count} total, showing ${anomaly.graph.edges?.length ?? 0}.`} />
                )}
                <Divider sx={{ my: 1.5 }} />
              </>
            )}

            {/* F – Mini graph */}
            {miniGraphOption && (
              <>
                <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.8, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Network graph
                </Typography>
                <Box sx={{
                  border: "1px solid", borderColor: "divider",
                  borderRadius: 1, overflow: "hidden", height: 200,
                }}>
                  <ReactECharts
                    option={miniGraphOption}
                    style={{ height: "100%", width: "100%" }}
                    opts={{ renderer: "canvas" }}
                  />
                </Box>

                {/* G – Peers summary */}
                <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                  <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                    Peers: <Box component="span" sx={{ color: "text.primary" }}>{anomaly.graph?.total_peer_count ?? "-"}</Box>
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                    Edges: <Box component="span" sx={{ color: "text.primary" }}>{anomaly.graph?.total_edge_count ?? "-"}</Box>
                  </Typography>
                  {allDstIps.length > 0 && (
                    <Typography sx={{ fontSize: 13, color: "text.secondary", flex: 1 }} noWrap>
                      Dest: <Box component="span" sx={{ color: "#7eadbb", fontFamily: "monospace" }}>{allDstIps.join(", ")}</Box>
                    </Typography>
                  )}
                </Stack>
                {anomaly.graph?.peers_truncated && (
                  <TruncatedWarning message="Peer list truncated — this host may have more destination peers than shown." />
                )}
              </>
            )}
          </Box>
        </>
      )}
      <BlacklistRuleDialog
        open={Boolean(blocklistEdge)}
        setOpen={(v) => { if (!v) setBlocklistEdge(null); }}
        row={blocklistEdge ? { src_ip: blocklistEdge.src, dest_ip: blocklistEdge.dst } : null}
        namePrefix="Tires-IA blocklist"
        initialPort={blocklistEdge?.ports?.length === 1 ? String(blocklistEdge.ports[0]) : "ANY"}
      />
    </Drawer>
  );
}
